import { useEffect } from 'react';
import { usePerpMarketsLookup } from 'src/hooks/perpMarkets/usePerpMarketsLookup';
import { useSpotMarketsLookup } from 'src/hooks/spotMarkets/useSpotMarketsLookup';
import { useSwitchMarket } from 'src/hooks/driftActions/market/useSwitchMarket';
import useDriftClient from 'src/hooks/useDriftClient';
import useDriftClientIsReady from 'src/hooks/useDriftClientIsReady';
import { PRELAUNCH_MARKET_RESOURCES } from 'src/environmentVariables/EnvironmentVariables';
import useAlertNotify from 'src/components/Toasts/AlertNotifications/hooks/useAlertNotify';
import { MarketId, MARKET_UTILS } from '@drift/common';

export default function usePreLaunchMarketAlertNotificationListener() {
	const alertNotify = useAlertNotify();
	const perpMarketsLookup = usePerpMarketsLookup();
	const spotMarketsLookup = useSpotMarketsLookup();
	const { switchMarket } = useSwitchMarket();
	const driftClient = useDriftClient();
	const driftClientIsReady = useDriftClientIsReady();

	const preLaunchMarketAlertToShow = getPreLaunchMarketAlertToShow();

	useEffect(() => {
		if (!preLaunchMarketAlertToShow || !driftClientIsReady || !driftClient) {
			return;
		}

		const { marketId } = preLaunchMarketAlertToShow;

		const { symbol } = marketId.isPerp
			? perpMarketsLookup[marketId.marketIndex]
			: spotMarketsLookup[marketId.marketIndex];

		const { maxLeverage } = MARKET_UTILS.getMaxLeverageForMarket(
			marketId.marketType,
			marketId.marketIndex,
			driftClient
		);

		alertNotify({
			id: `pre-launch-market-alert-${marketId.key}`,
			alertType: 'minor',
			message: `Pre-Launch Market: ${symbol}`,
			description: `${symbol} is now available to trade as a Pre-Launch Market with up to ${maxLeverage}x leverage.`,
			action: {
				label: `Trade ${symbol}`,
				callback: () => {
					switchMarket({
						marketIndex: marketId.marketIndex,
						marketType: marketId.marketType,
					});
				},
			},
		});
	}, [
		preLaunchMarketAlertToShow,
		driftClientIsReady,
		driftClient,
		perpMarketsLookup,
		spotMarketsLookup,
		switchMarket,
		alertNotify,
	]);
}

const getPreLaunchMarketAlertToShow = () => {
	return PRELAUNCH_MARKET_RESOURCES.find((market) => {
		return (
			Date.now() > market.startTs &&
			Date.now() < market.endTs &&
			!hasSeenPreLaunchMarketAlertForMarket(market.marketId)
		);
	});
};

const hasSeenPreLaunchMarketAlertForMarket = (marketId: MarketId) =>
	window?.localStorage
		? !!window?.localStorage.getItem(getHasSeenAlertKey(marketId))
		: false;

const getHasSeenAlertKey = (marketId: MarketId) =>
	`seen_pre_market_alert::${marketId.key}`;
